import { account } from '../core/account.js';
import { t } from '../core/i18n.js';
import { escapeHtml } from '../core/utils.js';

/* Pestaña «Fuentes» de Ajustes: la lista de fuentes con su resumen, filtros y estadísticas, y la pantalla de una fuente.
   Aquí solo se pinta: settings.js prepara los datos de cada fuente («vm») y atiende los clics. Cada fuente:
     { id, name, icon, subtitle, current, active, status: 'ok' | 'issue' | 'off', stateKey, stateLabel, free, shared,
       counts: {live, vod, series} | null, updatedAt, expiry: {text, days, expired} | null, errorText, loading }  */

export const FILTERS = ['all', 'active', 'issues', 'off'];

/** Cuántas fuentes hay de cada cosa (para las cajas de arriba). */
export function summary(list) {
    return {
        all: list.length,
        active: list.filter((v) => v.active && v.status !== 'issue').length,
        issues: list.filter((v) => v.status === 'issue').length,
        off: list.filter((v) => !v.active).length
    };
}

/** Qué fuentes se ven con un filtro y un texto de búsqueda. */
export function filterList(list, filter = 'all', query = '') {
    const q = String(query || '').trim().toLowerCase();
    return list.filter((v) => {
        if (filter === 'active' && !(v.active && v.status !== 'issue')) return false;
        if (filter === 'issues' && v.status !== 'issue') return false;
        if (filter === 'off' && v.active) return false;
        if (q && (v.name + ' ' + v.subtitle).toLowerCase().indexOf(q) === -1) return false;
        return true;
    });
}

/** 7265 → «7.265» (el español no agrupa los de cuatro cifras, pero en la lista se lee mejor con el punto). */
export function fmtNum(n, lang) {
    if (n == null) return '—';
    const s = Number(n).toLocaleString(lang || undefined);
    if (Number(n) >= 1000 && Number(n) < 10000 && /^\d+$/.test(s)) {
        const l = String(lang || '');
        const sep = /^(en|ja|zh|ko)/.test(l) ? ',' : /^fr/.test(l) ? '\u202f' : '.';
        return s.slice(0, -3) + sep + s.slice(-3);
    }
    return s;
}

const num = fmtNum;

/** El orden de las cifras es siempre el mismo en todas las filas (canales, películas, series): así se leen en columna. */
export function statOrder() {
    return ['live', 'vod', 'series'];
}

const STAT = { live: { icon: 'live_tv', label: 'src.channels' }, vod: { icon: 'movie', label: 'src.movies' }, series: { icon: 'tv', label: 'src.series' } };

function statsHtml(vm, lang) {
    return statOrder(vm).map((k) => `<span class="src-stat is-${k}"><span class="mi">${STAT[k].icon}</span><span class="src-stat-n"><b>${num(vm.counts && vm.status !== 'issue' ? vm.counts[k] : null, lang)}</b><small>${escapeHtml(t(STAT[k].label))}</small></span></span>`).join('');
}

function badgesHtml(vm) {
    const out = [];
    if (vm.free) out.push(`<span class="src-badge is-free">${escapeHtml(t('src.free'))}</span>`);
    if (vm.current) out.push(`<span class="src-badge is-main">${escapeHtml(t('src.main'))}</span>`);
    else if (vm.active) out.push(`<span class="src-badge">${escapeHtml(t('src.inCatalog'))}</span>`);
    if (vm.stateKey && !vm.free && vm.stateKey !== 'unknown' && vm.stateKey !== 'list') out.push(`<span class="src-badge is-state is-${vm.stateKey}">${escapeHtml(vm.stateLabel)}</span>`);
    if (!vm.active) out.push(`<span class="src-badge is-off">${escapeHtml(t('src.offBadge'))}</span>`);
    return out.join('');
}

function metaHtml(vm, { tag = true } = {}) {
    const out = [];
    if (vm.updatedAt) {
        const when = new Date(vm.updatedAt).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
        // Con incidencias, donde iría «Actualizado» va la etiqueta «Reintentar» (se pulsa, pero no parece un botón).
        if (vm.status === 'issue' && tag) out.push(`<span class="src-meta-item is-retry" role="button" tabindex="0" data-src-retry="${escapeHtml(vm.id)}" title="${escapeHtml(t('src.lastTry') + ': ' + when)}"><span class="mi">refresh</span><span><small>${escapeHtml(t('src.retry'))}</small><b>${escapeHtml(when)}</b></span></span>`);
        else out.push(`<span class="src-meta-item ${vm.stale ? 'is-warn' : ''}"><span class="mi">${vm.status === 'issue' ? 'schedule' : 'refresh'}</span><span><small>${escapeHtml(t(vm.status === 'issue' ? 'src.lastTry' : 'src.updated'))}</small><b>${escapeHtml(when)}</b></span></span>`);
    }
    if (vm.expiry && vm.expiry.text) out.push(`<span class="src-meta-item ${vm.expiry.expired ? 'is-warn' : ''}"><span class="mi">event</span><span><small>${escapeHtml(vm.expiry.label || '')}</small><b>${escapeHtml(vm.expiry.text)}</b></span></span>`);
    return out.join('');
}

/** Una fila (en pantalla ancha, una fila de tabla; en el móvil, una tarjeta). */
export function rowHtml(vm, lang) {
    const issue = vm.status === 'issue';
    return `<div class="src-row is-${vm.status} ${vm.current ? 'is-current' : ''} ${vm.active ? '' : 'is-off'} ${vm.open ? 'is-open' : ''}" data-provider="${escapeHtml(vm.id)}" data-src-open="${escapeHtml(vm.id)}" tabindex="0" role="button" aria-label="${escapeHtml(vm.name)}">
        <span class="src-icon"><span class="mi">${vm.icon}</span></span>
        <div class="src-main">
            <div class="src-title"><span class="src-name">${escapeHtml(vm.name)}</span>${badgesHtml(vm)}</div>
            <div class="src-sub">${escapeHtml(vm.subtitle)}</div>
            ${issue && vm.errorText ? `<div class="src-warn"><span class="mi">warning</span>${escapeHtml(vm.errorText)}</div>` : ''}
        </div>
        <button type="button" class="src-toggle" data-src-more="${escapeHtml(vm.id)}" aria-expanded="${!!vm.open}"><span class="src-toggle-text">${escapeHtml(t(vm.open ? 'src.less' : 'src.more'))}</span><span class="mi">expand_more</span></button>
        <div class="src-stats">${statsHtml(vm, lang)}</div>
        <div class="src-meta">${metaHtml(vm)}</div>
        <div class="src-actions" data-nav-row data-nav-reach>
            <button type="button" class="provider-switch src-switch" role="switch" aria-checked="${vm.active}" data-provider-toggle="${escapeHtml(vm.id)}" aria-label="${escapeHtml(t('settings.providerSwitch', { name: vm.name }))}"><span class="switch ${vm.active ? 'is-on' : ''}" aria-hidden="true"></span></button>
            <button type="button" class="btn btn-icon src-more" data-provider-more="${escapeHtml(vm.id)}" aria-haspopup="true" aria-label="${escapeHtml(t('common.moreOptions') + ': ' + vm.name)}"><span class="mi">more_vert</span></button>
        </div>
    </div>`;
}

/** Las cuatro cajas de resumen (todas / activas / con problemas / apagadas). `filter` = la que está marcada (null: ninguna). */
export function summaryHtml(list, filter = null) {
    const sum = summary(list);
    const box = (id, label) => `<button type="button" class="src-sum ${filter === id ? 'is-active' : ''}" data-src-filter="${id}" aria-pressed="${filter === id}"><span class="src-sum-n ${id === 'issues' && sum.issues ? 'is-warn' : ''}">${id === 'all' ? '' : `<i class="src-dot is-${id}"></i>`}<b>${sum[id]}</b></span><small>${escapeHtml(label)}</small></button>`;
    return `<div class="src-summary" role="group">${box('all', t('src.f.all'))}${box('active', t('src.f.active'))}${box('issues', t('src.f.issues'))}${box('off', t('src.f.off'))}</div>`;
}

/** La lista de fuentes que se ve con un filtro y un texto de búsqueda (solo las filas). */
export function listHtml(list, { filter = 'all', query = '', lang } = {}) {
    const shown = filterList(list, filter, query);
    return shown.length ? shown.map((v) => rowHtml(v, lang)).join('') : `<p class="src-empty">${escapeHtml(t('src.empty'))}</p>`;
}

/** La pestaña: cabecera con las cifras y los botones. La lista de fuentes no está aquí: se abre en una ventana («Ver fuentes»). */
export function panelHtml(list, { lang } = {}) {
    void lang;
    return `<div class="settings-group providers-group src-panel" data-tab="sources">
        <div class="src-head">
            <span class="src-head-icon"><span class="mi">storage</span></span>
            <div class="src-head-text"><h2>${escapeHtml(t('src.title'))}</h2><p>${escapeHtml(t('src.sub'))}</p></div>
            ${summaryHtml(list)}
            ${account.demo ? '' : `<button type="button" class="src-add" data-setting="addsource"><span class="mi">add</span><span class="src-add-text is-long">${escapeHtml(t('src.add'))}</span><span class="src-add-text is-short">${escapeHtml(t('src.addShort'))}</span></button>`}
            <button type="button" class="src-view" data-src-view ${list.length ? '' : 'disabled'}><span class="mi">view_list</span><span>${escapeHtml(t('src.view', { n: list.length }))}</span><span class="mi src-view-go">chevron_right</span></button>
        </div>
    </div>`;
}

/** El contenido de la ventana «Fuentes»: cajas de resumen que filtran, buscador y la lista. */
export function modalHtml(list, { filter = 'all', query = '', lang } = {}) {
    return `<div class="src-modal">
        <div class="src-modal-head" data-src-chips>${summaryHtml(list, filter)}
            <label class="src-search"><span class="mi">search</span><input type="search" data-src-search placeholder="${escapeHtml(t('src.search'))}" value="${escapeHtml(query)}" autocomplete="off" spellcheck="false"/></label>
        </div>
        <div class="src-list" data-nav-list>${listHtml(list, { filter, query, lang })}</div>
    </div>`;
}

/** La pantalla de una fuente (ventana modal). `info`: { rows: [[etiqueta, valor, {ok?}]], content: [[etiqueta, valor]], actions: [{ id, icon, label, danger }] }. */
export function detailHtml(vm, info, lang) {
    const stat = (k) => `<div class="src-box is-${k}"><span class="mi">${STAT[k].icon}</span><b>${num(vm.counts ? vm.counts[k] : null, lang)}</b><small>${escapeHtml(t(STAT[k].label))}</small></div>`;
    const list = (rows) => rows.map(([label, value, o]) => `<div class="src-kv"><span>${escapeHtml(label)}</span><b class="${o && o.ok ? 'is-ok' : ''} ${o && o.warn ? 'is-warn' : ''}">${o && o.ok ? '<i class="src-dot is-active"></i>' : ''}${escapeHtml(String(value))}</b></div>`).join('');
    return `<div class="src-detail" data-g="${vm.status}">
        <div class="src-detail-card is-${vm.status}">
            <div class="src-detail-top">
                <span class="src-icon"><span class="mi">${vm.icon}</span></span>
                <div class="src-main"><div class="src-name">${escapeHtml(vm.name)}</div><div class="src-title">${badgesHtml(vm)}</div><div class="src-sub">${escapeHtml(vm.subtitle)}</div></div>
                <button type="button" class="provider-switch src-switch" role="switch" aria-checked="${vm.active}" data-provider-toggle="${escapeHtml(vm.id)}" aria-label="${escapeHtml(t('settings.providerSwitch', { name: vm.name }))}"><span class="switch ${vm.active ? 'is-on' : ''}" aria-hidden="true"></span></button>
            </div>
            ${vm.status === 'issue' && vm.errorText ? `<div class="src-warn"><span class="mi">warning</span>${escapeHtml(vm.errorText)}</div>` : ''}
            <div class="src-boxes">${statOrder(vm).map(stat).join('')}</div>
            <div class="src-detail-meta">${metaHtml(vm, { tag: false })}</div>
            <button type="button" class="btn btn-block src-refresh" data-src-refresh="${escapeHtml(vm.id)}"><span class="mi">refresh</span>${escapeHtml(t('src.refresh'))}</button>
        </div>
        <div class="src-section">${escapeHtml(t('src.info'))}</div>
        <div class="src-card">${list(info.rows)}</div>
        ${info.content.length ? `<div class="src-section">${escapeHtml(t('src.content'))}</div><div class="src-card">${list(info.content)}</div>` : ''}
        <div class="src-section">${escapeHtml(t('src.actions'))}</div>
        <div class="src-card">${info.actions.map((a) => `<button type="button" class="src-action ${a.danger ? 'is-danger' : ''}" data-src-action="${a.id}"><span class="mi">${a.icon}</span><span>${escapeHtml(a.label)}</span></button>`).join('')}</div>
    </div>`;
}
